import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { Plus, X, Bell, Settings, MessageSquare, Users, Clock, Star, Zap, AlertCircle, ExternalLink, RefreshCw, Loader2, Edit2, ChevronDown, ChevronUp, Calendar, MapPin, DollarSign, Phone, AlertTriangle, Archive, Filter } from 'lucide-react';
import SavedRecommendations from './components/SavedRecommendations';
import CheckoutSuccess from './components/CheckoutSuccess';
import CheckoutCancel from './components/CheckoutCancel';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://slack-ai-worker.zlceapps.workers.dev';

// Main Dashboard Component
const Dashboard = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('recommendations');
  const [preferences, setPreferences] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [monitoredChannels, setMonitoredChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [fetchingRecommendations, setFetchingRecommendations] = useState(false);
  const [fetchDays, setFetchDays] = useState(3);
  const [editingPreference, setEditingPreference] = useState(null);
  const [expandedRecommendations, setExpandedRecommendations] = useState(new Set());
  const [hiddenRecommendations, setHiddenRecommendations] = useState(new Set());
  const [newPreference, setNewPreference] = useState({
    keyword: '',
    description: '',
    channels: [],
    urgency: 'medium'
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [savedRecommendationIds, setSavedRecommendationIds] = useState(new Set());
  const [refreshSavedRecommendations, setRefreshSavedRecommendations] = useState(null);
  const [selectedPreferencesForScanning, setSelectedPreferencesForScanning] = useState(new Set());
  
  // Subscription states
  const [subscriptionStatus, setSubscriptionStatus] = useState('trial');
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    preference: 'all',
    channel: 'all',
    confidence: 'all',
    hasPrice: false,
    hasLocation: false,
    hasDate: false,
    hasLinks: false,
    searchText: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Real-time polling states
  const [lastRecommendationCount, setLastRecommendationCount] = useState(0);
  const [hasNewRecommendations, setHasNewRecommendations] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(null);
  

  // Track page visibility for smart polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Smart polling: more frequent when page is visible and user is active
  useEffect(() => {
    if (userId) {
      fetchUserDataSequentially();
      checkSubscriptionStatus();
      
      // Clear any existing interval
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      
      // Set up smart polling based on page visibility
      const pollInterval = isPageVisible ? 60000 : 120000; // 30s when visible, 2min when hidden
      console.log(`🔄 Setting up polling interval: ${pollInterval/1000}s (page visible: ${isPageVisible})`);
      
      const interval = setInterval(() => {
        fetchRecommendationsWithNewDetection();
      }, pollInterval);
      
      setPollingInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [userId, isPageVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize selected preferences when preferences are loaded
  useEffect(() => {
    if (preferences.length > 0) {
      const activePreferenceIds = new Set(preferences.filter(p => p.active).map(p => p.id));
      setSelectedPreferencesForScanning(activePreferenceIds);
    }
  }, [preferences]);

  const fetchUserDataSequentially = async () => {
    try {
      // Load critical data first
      await fetchPreferences();
      await fetchRecommendations();
      await loadSavedRecommendationIds();
      
      // Load channels data with delay to avoid rate limiting
      setTimeout(() => {
        fetchChannels();
      }, 500);
      
      setTimeout(() => {
        fetchMonitoredChannels();
      }, 1000);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const loadSavedRecommendationIds = async () => {
    try {
      console.log(`📋 Loading saved recommendation IDs for user ${userId}`);
      
      // Reset to empty set first
      console.log(`🔄 Resetting savedRecommendationIds to empty set`);
      setSavedRecommendationIds(new Set());
      
      const response = await fetch(`${API_BASE}/api/saved-recommendations/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });
      console.log(`📡 API response status: ${response.status}`);
      
      if (response.ok) {
        const savedRecs = await response.json();
        console.log(`📊 Raw saved recommendations from API:`, savedRecs);
        
        // Ensure IDs are converted to the same type as recommendation IDs
        const savedIds = new Set(savedRecs.map(rec => {
          const id = rec.recommendation_id;
          // Convert to number if it's a string number, otherwise keep as-is
          return typeof id === 'string' && !isNaN(id) ? parseInt(id) : id;
        }));
        console.log(`🔢 Extracted recommendation IDs:`, Array.from(savedIds));
        
        setSavedRecommendationIds(savedIds);
        console.log(`✅ Set savedRecommendationIds state to:`, Array.from(savedIds));
      } else {
        console.log(`⚠️ Failed to load saved recommendations: ${response.status}`);
        setSavedRecommendationIds(new Set()); // Ensure empty set on failure
      }
    } catch (error) {
      console.error('❌ Error loading saved recommendation IDs:', error);
      setSavedRecommendationIds(new Set()); // Ensure empty set on error
    }
  };

  const fetchPreferences = async () => {
    const response = await fetch(`${API_BASE}/api/preferences/${userId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
      }
    });
    if (response.ok) {
      const data = await response.json();
      setPreferences(data);
    }
  };

  const fetchRecommendations = async () => {
    const response = await fetch(`${API_BASE}/api/recommendations/${userId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
      }
    });
    if (response.ok) {
      const data = await response.json();
      setRecommendations(data);
    }
  };

  // Enhanced function that detects new recommendations and shows notifications
  const fetchRecommendationsWithNewDetection = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/recommendations/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const newCount = data.length;
        
        // Check if we have new recommendations (more than before)
        if (lastRecommendationCount > 0 && newCount > lastRecommendationCount) {
          const newRecommendationsCount = newCount - lastRecommendationCount;
          setHasNewRecommendations(true);
          
          // Show notification for new recommendations
          if (!isPageVisible) {
            // Show browser notification if page is not visible
            showDesktopNotification(newRecommendationsCount);
          } else {
            // Show toast notification if page is visible
            toast.success(`🔔 ${newRecommendationsCount} new recommendation${newRecommendationsCount > 1 ? 's' : ''} found!`, {
              duration: 5000,
              icon: '🎯'
            });
          }
          
          console.log(`🔔 Detected ${newRecommendationsCount} new recommendations (${lastRecommendationCount} → ${newCount})`);
        }
        
        setRecommendations(data);
        setLastRecommendationCount(newCount);
      }
    } catch (error) {
      console.error('Error fetching recommendations with detection:', error);
      // Fallback to regular fetch
      fetchRecommendations();
    }
  };

  // Request notification permission and show desktop notification
  const showDesktopNotification = (count) => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification('Slack Assistant - New Recommendations', {
          body: `${count} new recommendation${count > 1 ? 's' : ''} found matching your preferences!`,
          icon: '/favicon.ico',
          tag: 'slack-assistant-new-recs',
          requireInteraction: false
        });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            showDesktopNotification(count);
          }
        });
      }
    }
  };

  // fetchRecommendationsWithNewDetection is called directly by the button

  const fetchChannels = async (type = 'joined') => {
    try {
      setChannelsLoading(true);
      const response = await fetch(`${API_BASE}/api/channels/${userId}?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
        // Only show success toast if we actually got data
        if (data.length > 0) {
          toast.success(`Loaded ${data.length} ${type === 'all' ? 'workspace' : 'joined'} channels`);
        }
      } else {
        // Don't show error toast for failed channel loads - they're cached
        console.error('Failed to load channels');
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      // Don't show error toast - channels will load from cache
    } finally {
      setChannelsLoading(false);
    }
  };

  const fetchMonitoredChannels = async (type = 'joined') => {
    try {
      const response = await fetch(`${API_BASE}/api/channels/${userId}?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMonitoredChannels(data);
      } else {
        console.error('Failed to load monitored channels');
      }
    } catch (error) {
      console.error('Error fetching monitored channels:', error);
    }
  };

  const togglePreferenceForScanning = (preferenceId) => {
    const newSelected = new Set(selectedPreferencesForScanning);
    if (newSelected.has(preferenceId)) {
      newSelected.delete(preferenceId);
    } else {
      newSelected.add(preferenceId);
    }
    setSelectedPreferencesForScanning(newSelected);
  };

  const fetchRecommendationsManually = async () => {
    if (preferences.length === 0) {
      toast.error('Please add some preferences first before fetching recommendations');
      return;
    }
    
    if (selectedPreferencesForScanning.size === 0) {
      toast.error('Please select at least one preference to scan for');
      return;
    }
    
    try {
      setFetchingRecommendations(true);
      const selectedPreferenceIds = Array.from(selectedPreferencesForScanning);
      const response = await fetch(`${API_BASE}/api/recommendations/${userId}/fetch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({
          days: fetchDays,
          preferenceIds: selectedPreferenceIds
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Real-time processing completed immediately
        toast.success(
          `Analysis complete! Processed ${result.messagesProcessed} messages and found ${result.newRecommendations} new recommendations from the last ${result.daysAnalyzed} days.`,
          { duration: 6000 }
        );
        // Refresh recommendations with new detection and clear new indicator
        await fetchRecommendationsWithNewDetection();
        setHasNewRecommendations(false);
      } else {
        const error = await response.json();
        toast.error(`Failed to fetch recommendations: ${error.error}`);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to fetch recommendations');
    } finally {
      setFetchingRecommendations(false);
    }
  };


  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const response = await fetch(`${API_BASE}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || 'temp-token'}`
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const error = await response.json();
        toast.error(`Failed to create checkout session: ${error.error}`);
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error('Failed to start upgrade process');
    } finally {
      setIsUpgrading(false);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      // TODO: Implement subscription status check
      // For now, simulate trial period
      const trialStart = localStorage.getItem('trial_start');
      if (!trialStart) {
        localStorage.setItem('trial_start', new Date().toISOString());
        setSubscriptionStatus('trial');
        const trialEnd = new Date();
        trialEnd.setDate(trialEnd.getDate() + 7); // 7-day trial
        setTrialEndsAt(trialEnd);
      } else {
        const trialStartDate = new Date(trialStart);
        const trialEndDate = new Date(trialStartDate);
        trialEndDate.setDate(trialEndDate.getDate() + 7);
        
        if (new Date() > trialEndDate) {
          setSubscriptionStatus('trial_expired');
        } else {
          setSubscriptionStatus('trial');
          setTrialEndsAt(trialEndDate);
        }
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const fetchChannelsForUser = async (targetUserId) => {
    if (!targetUserId.trim()) {
      toast.error('Please enter a user ID');
      return;
    }
    
    try {
      setChannelsLoading(true);
      const response = await fetch(`${API_BASE}/api/channels/${userId}/user/${targetUserId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setChannels(data);
        toast.success(`Loaded ${data.length} channels where ${targetUserId} is a member`);
      } else {
        const error = await response.json();
        toast.error(`Failed to load channels for user: ${error.error}`);
      }
    } catch (error) {
      console.error('Error fetching user channels:', error);
      toast.error('Failed to load channels for user');
    } finally {
      setChannelsLoading(false);
    }
  };

  const addPreference = async () => {
    if (!newPreference.keyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({
          userId,
          ...newPreference
        })
      });

      if (response.ok) {
        // Auto-join selected channels
        if (newPreference.channels.length > 0) {
          toast.loading('Joining selected channels...', { id: 'joining-channels' });
          
          let joinedCount = 0;
          let failedCount = 0;
          
          for (const channelId of newPreference.channels) {
            try {
              const joinResponse = await fetch(`${API_BASE}/api/channels/${userId}/join`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
                },
                body: JSON.stringify({ channelId })
              });
              
              if (joinResponse.ok) {
                joinedCount++;
              } else {
                failedCount++;
                const error = await joinResponse.json();
                console.log(`Failed to join channel ${channelId}:`, error.error);
              }
            } catch (joinError) {
              failedCount++;
              console.log(`Error joining channel ${channelId}:`, joinError);
            }
          }
          
          toast.dismiss('joining-channels');
          
          if (joinedCount > 0 && failedCount === 0) {
            toast.success(`Preference added and automatically joined ${joinedCount} channels!`);
          } else if (joinedCount > 0 && failedCount > 0) {
            toast.success(`Preference added! Joined ${joinedCount} channels (${failedCount} failed - you may already be a member)`);
          } else if (failedCount > 0) {
            toast.success('Preference added! Note: Could not join some channels (you may already be a member)');
          } else {
            toast.success('Preference added successfully!');
          }
        } else {
          toast.success('Preference added successfully!');
        }
        
        setNewPreference({ keyword: '', description: '', channels: [], urgency: 'medium' });
        setShowAddForm(false);
        fetchPreferences();
        
        // Refresh monitored channels to show new channels from this preference
        fetchMonitoredChannels();
      } else {
        toast.error('Failed to add preference');
      }
    } catch (error) {
      console.error('Error adding preference:', error);
      toast.error('Failed to add preference');
    }
  };

  const deletePreference = async (preferenceId) => {
    try {
      const response = await fetch(`${API_BASE}/api/preferences/${preferenceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });

      if (response.ok) {
        toast.success('Preference deleted successfully!');
        fetchPreferences();
      } else {
        toast.error('Failed to delete preference');
      }
    } catch (error) {
      console.error('Error deleting preference:', error);
      toast.error('Failed to delete preference');
    }
  };

  const startEditingPreference = (preference) => {
    setEditingPreference({
      ...preference,
      channels: JSON.parse(preference.channels || '[]')
    });
  };

  const updatePreference = async () => {
    if (!editingPreference.keyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/preferences/${editingPreference.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({
          keyword: editingPreference.keyword,
          description: editingPreference.description,
          channels: JSON.stringify(editingPreference.channels),
          urgency: editingPreference.urgency,
          active: editingPreference.active
        })
      });

      if (response.ok) {
        // Auto-join selected channels
        if (editingPreference.channels.length > 0) {
          toast.loading('Joining selected channels...', { id: 'joining-channels' });
          
          let joinedCount = 0;
          let failedCount = 0;
          
          for (const channelId of editingPreference.channels) {
            try {
              const joinResponse = await fetch(`${API_BASE}/api/channels/${userId}/join`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
                },
                body: JSON.stringify({ channelId })
              });
              
              if (joinResponse.ok) {
                joinedCount++;
              } else {
                failedCount++;
                const error = await joinResponse.json();
                console.log(`Failed to join channel ${channelId}:`, error.error);
              }
            } catch (joinError) {
              failedCount++;
              console.log(`Error joining channel ${channelId}:`, joinError);
            }
          }
          
          toast.dismiss('joining-channels');
          
          if (joinedCount > 0 && failedCount === 0) {
            toast.success(`Preference updated and automatically joined ${joinedCount} channels!`);
          } else if (joinedCount > 0 && failedCount > 0) {
            toast.success(`Preference updated! Joined ${joinedCount} channels (${failedCount} failed - you may already be a member)`);
          } else if (failedCount > 0) {
            toast.success('Preference updated! Note: Could not join some channels (you may already be a member)');
          } else {
            toast.success('Preference updated successfully!');
          }
        } else {
          toast.success('Preference updated successfully!');
        }
        
        setEditingPreference(null);
        fetchPreferences();
        
        // Refresh monitored channels to show updated channels from this preference
        fetchMonitoredChannels();
      } else {
        toast.error('Failed to update preference');
      }
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update preference');
    }
  };

  const openInSlack = (channelId, messageTs) => {
    // Construct Slack deep link
    const slackUrl = `slack://channel?team=YOUR_TEAM_ID&id=${channelId}&message=${messageTs}`;
    window.open(slackUrl, '_blank');
  };

  const refreshSlackConnection = () => {
    toast.loading('Refreshing Slack connection...', { id: 'refresh' });
    // Store current user ID to restore after OAuth
    localStorage.setItem('slack_user_id_temp', userId);
    // Redirect to OAuth with a refresh parameter
    window.location.href = `${API_BASE}/auth/slack?refresh=true`;
  };

  const joinChannel = async (channelId, channelName) => {
    try {
      const response = await fetch(`${API_BASE}/api/channels/${userId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({ channelId })
      });

      if (response.ok) {
        toast.success(`Successfully joined #${channelName}!`);
        // Refresh monitored channels to update the joined status
        fetchMonitoredChannels();
      } else {
        const error = await response.json();
        
        // Handle scope issues specifically
        if (error.details && error.details.error === 'missing_scope') {
          toast.error(
            `Missing permission to join channels. Please reconnect to Slack to grant additional permissions.`,
            { duration: 6000 }
          );
        } else {
          toast.error(`Failed to join #${channelName}: ${error.error}`);
        }
      }
    } catch (error) {
      console.error('Error joining channel:', error);
      toast.error(`Failed to join #${channelName}`);
    }
  };

  const leaveChannel = async (channelId, channelName) => {
    try {
      const response = await fetch(`${API_BASE}/api/channels/${userId}/leave`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({ channelId })
      });

      if (response.ok) {
        toast.success(`Successfully left #${channelName}!`);
        // Refresh monitored channels to update the joined status
        fetchMonitoredChannels();
      } else {
        const error = await response.json();
        toast.error(`Failed to leave #${channelName}: ${error.error}`);
      }
    } catch (error) {
      console.error('Error leaving channel:', error);
      toast.error(`Failed to leave #${channelName}`);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch(urgency) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-green-600 bg-green-50';
    if (confidence >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  const toggleRecommendationExpansion = (recId) => {
    const newExpanded = new Set(expandedRecommendations);
    if (newExpanded.has(recId)) {
      newExpanded.delete(recId);
    } else {
      newExpanded.add(recId);
    }
    setExpandedRecommendations(newExpanded);
  };

  // eslint-disable-next-line no-unused-vars
  const hideRecommendation = (recId) => {
    const newHidden = new Set(hiddenRecommendations);
    newHidden.add(recId);
    setHiddenRecommendations(newHidden);
    toast.success('Recommendation hidden');
  };

  const saveRecommendation = async (recommendationId) => {
    console.log(`🔄 Attempting to save recommendation ${recommendationId} for user ${userId}`);
    
    try {
      const response = await fetch(`${API_BASE}/api/recommendations/${recommendationId}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({ userId })
      });

      console.log(`📡 Save response status: ${response.status}`);

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Save successful:`, result);
        
        const newSaved = new Set(savedRecommendationIds);
        // Ensure consistent type - convert to number if it's a string number
        const normalizedId = typeof recommendationId === 'string' && !isNaN(recommendationId) ? parseInt(recommendationId) : recommendationId;
        newSaved.add(normalizedId);
        setSavedRecommendationIds(newSaved);
        
        // Remove from recommendations list
        setRecommendations(prev => prev.filter(rec => rec.id !== recommendationId));
        
        toast.success('Recommendation saved!');
        
        // Refresh the saved recommendations list
        if (refreshSavedRecommendations) {
          console.log('📋 Refreshing saved recommendations list');
          refreshSavedRecommendations();
        }
      } else {
        const errorData = await response.json();
        console.error(`❌ Save failed:`, errorData);
        
        // Check if it's already saved error
        if (errorData.error && errorData.error.includes('already saved')) {
          toast.success('Recommendation was already saved!');
          // Still update the UI to reflect saved state
          const newSaved = new Set(savedRecommendationIds);
          const normalizedId = typeof recommendationId === 'string' && !isNaN(recommendationId) ? parseInt(recommendationId) : recommendationId;
          newSaved.add(normalizedId);
          setSavedRecommendationIds(newSaved);
          setRecommendations(prev => prev.filter(rec => rec.id !== recommendationId));
        } else {
          toast.error(`Failed to save recommendation: ${errorData.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('❌ Error saving recommendation:', error);
      toast.error(`Failed to save recommendation: ${error.message}`);
    }
  };

  const markNotInterested = async (recommendationId) => {
    console.log(`👎 Marking recommendation ${recommendationId} as not interested for user ${userId}`);
    
    try {
      const response = await fetch(`${API_BASE}/api/recommendations/${recommendationId}/not-interested`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        console.log(`✅ Successfully marked recommendation ${recommendationId} as not interested`);
        
        // Remove from recommendations list
        setRecommendations(prev => prev.filter(rec => rec.id !== recommendationId));
        
        toast.success('Marked as not interested');
      } else {
        const errorData = await response.json();
        console.error(`❌ Failed to mark as not interested:`, errorData);
        toast.error(`Failed to mark as not interested: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error marking as not interested:', error);
      toast.error(`Failed to mark as not interested: ${error.message}`);
    }
  };

  const clearAllRecommendations = async () => {
    console.log(`🗑️ Clearing all recommendations for user ${userId}`);
    
    try {
      const response = await fetch(`${API_BASE}/api/recommendations/${userId}/clear-all`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ Successfully cleared ${result.deletedCount} recommendations`);
        
        // Clear the recommendations list
        setRecommendations([]);
        
        toast.success(`Cleared ${result.deletedCount} recommendations`);
      } else {
        const errorData = await response.json();
        console.error(`❌ Failed to clear recommendations:`, errorData);
        toast.error(`Failed to clear recommendations: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error clearing recommendations:', error);
      toast.error(`Failed to clear recommendations: ${error.message}`);
    }
  };

  const handleUnsaveRecommendation = async (recommendationId) => {
    console.log(`🔄 Recommendation ${recommendationId} was unsaved, refreshing data`);
    
    // Remove from saved set with type consistency
    const newSaved = new Set(savedRecommendationIds);
    
    // Try both the original ID and normalized versions to ensure removal
    newSaved.delete(recommendationId);
    const normalizedId = typeof recommendationId === 'string' && !isNaN(recommendationId) ? parseInt(recommendationId) : recommendationId;
    newSaved.delete(normalizedId);
    
    // Also try string version if it was a number
    if (typeof recommendationId === 'number') {
      newSaved.delete(String(recommendationId));
    }
    
    console.log(`🗑️ Removed recommendation ${recommendationId} from saved set. New size: ${newSaved.size}`);
    setSavedRecommendationIds(newSaved);
    
    // Refresh recommendations to potentially show the unsaved item again
    await fetchRecommendations();
    
    // Also refresh the saved recommendations list to ensure it's up to date
    await loadSavedRecommendationIds();
  };

  const cleanText = (text) => {
    if (!text) return text;
    
    return text
      // Remove Slack emoji syntax like :tada:, :smile:, etc.
      .replace(/:[a-zA-Z0-9_+-]+:/g, '')
      // Remove code blocks and backticks
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`[^`]*`/g, '')
      // Remove excessive punctuation
      .replace(/[!]{2,}/g, '!')
      .replace(/[?]{2,}/g, '?')
      .replace(/[.]{3,}/g, '...')
      // Clean up extra whitespace
      .replace(/\s+/g, ' ')
      .trim();
  };

  const parseMessageData = (messageText) => {
    try {
      const parsed = JSON.parse(messageText);
      return {
        originalText: cleanText(parsed.originalText || messageText),
        title: cleanText(parsed.title || parsed.summary || 'Slack Message'),
        snippet: cleanText(parsed.snippet || parsed.summary || messageText.substring(0, 150) + '...'),
        extractedInfo: parsed.extractedInfo || {}
      };
    } catch (e) {
      // Fallback for old format - create a basic title from the message
      const cleanedText = cleanText(messageText);
      const words = cleanedText.split(' ').slice(0, 8).join(' '); // First 8 words
      const title = words.length > 50 ? words.substring(0, 50) + '...' : words || 'Slack Message';
      
      return {
        originalText: cleanedText,
        title: title,
        snippet: cleanedText.substring(0, 150) + '...',
        extractedInfo: {}
      };
    }
  };

  const renderExtractedInfo = (extractedInfo) => {
    const items = [];
    
    // Combine date and time if both exist
    if (extractedInfo.eventDate || extractedInfo.eventTime) {
      let dateTimeValue = '';
      if (extractedInfo.eventDate && extractedInfo.eventTime) {
        dateTimeValue = `${extractedInfo.eventDate}, ${extractedInfo.eventTime}`;
      } else if (extractedInfo.eventDate) {
        dateTimeValue = extractedInfo.eventDate;
      } else if (extractedInfo.eventTime) {
        dateTimeValue = extractedInfo.eventTime;
      }
      
      items.push({
        icon: Calendar,
        label: 'Date & Time',
        value: dateTimeValue,
        color: 'text-blue-600'
      });
    }
    
    if (extractedInfo.location) {
      items.push({
        icon: MapPin,
        label: 'Location',
        value: extractedInfo.location,
        color: 'text-green-600'
      });
    }
    
    if (extractedInfo.price) {
      items.push({
        icon: DollarSign,
        label: 'Price',
        value: extractedInfo.price,
        color: 'text-purple-600'
      });
    }
    
    if (extractedInfo.contact) {
      items.push({
        icon: Phone,
        label: 'Contact',
        value: extractedInfo.contact,
        color: 'text-orange-600'
      });
    }
    
    if (extractedInfo.deadline) {
      items.push({
        icon: AlertTriangle,
        label: 'Deadline',
        value: extractedInfo.deadline,
        color: 'text-red-600'
      });
    }
    
    // Add links if they exist
    if (extractedInfo.links && extractedInfo.links.length > 0) {
      extractedInfo.links.forEach((link, index) => {
        items.push({
          icon: ExternalLink,
          label: link.label || 'Link',
          value: link.url,
          color: 'text-indigo-600',
          isLink: true
        });
      });
    }
    
    return items;
  };

  // Filter recommendations based on current filters
  const filterRecommendations = (recommendations) => {
    return recommendations.filter(rec => {
      // Skip hidden recommendations
      if (hiddenRecommendations.has(rec.id)) return false;
      
      const messageData = parseMessageData(rec.message_text);
      const extractedInfo = messageData.extractedInfo;
      
      // Filter by preference
      if (filters.preference !== 'all' && rec.preference_keyword !== filters.preference) {
        return false;
      }
      
      // Filter by channel
      if (filters.channel !== 'all' && rec.channel_name !== filters.channel) {
        return false;
      }
      
      // Filter by confidence
      if (filters.confidence !== 'all') {
        const confidence = rec.confidence;
        switch (filters.confidence) {
          case 'high':
            if (confidence < 90) return false;
            break;
          case 'medium':
            if (confidence < 70 || confidence >= 90) return false;
            break;
          case 'low':
            if (confidence >= 70) return false;
            break;
          default:
            break;
        }
      }
      
      // Filter by extracted information
      if (filters.hasPrice && !extractedInfo.price) return false;
      if (filters.hasLocation && !extractedInfo.location) return false;
      if (filters.hasDate && !extractedInfo.eventDate && !extractedInfo.eventTime) return false;
      if (filters.hasLinks && (!extractedInfo.links || extractedInfo.links.length === 0)) return false;
      
      // Filter by search text
      if (filters.searchText.trim()) {
        const searchLower = filters.searchText.toLowerCase();
        const titleMatch = messageData.title.toLowerCase().includes(searchLower);
        const snippetMatch = messageData.snippet.toLowerCase().includes(searchLower);
        const originalMatch = messageData.originalText.toLowerCase().includes(searchLower);
        const channelMatch = rec.channel_name.toLowerCase().includes(searchLower);
        const userMatch = rec.user_name.toLowerCase().includes(searchLower);
        
        if (!titleMatch && !snippetMatch && !originalMatch && !channelMatch && !userMatch) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      preference: 'all',
      channel: 'all',
      confidence: 'all',
      hasPrice: false,
      hasLocation: false,
      hasDate: false,
      hasLinks: false,
      searchText: ''
    });
  };

  // Get unique values for filter dropdowns
  const getUniquePreferences = () => {
    const prefs = new Set(recommendations.map(rec => rec.preference_keyword));
    return Array.from(prefs).sort();
  };

  const getUniqueChannels = () => {
    const channels = new Set(recommendations.map(rec => rec.channel_name));
    return Array.from(channels).sort();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                <Bell className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Kellogg Slack AI Assistant</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={refreshSlackConnection}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors border border-blue-200"
                title="Refresh Slack connection to get new permissions"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="text-sm font-medium">Reset Slack Connection</span>
              </button>
              <span className="text-sm text-green-600 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Connected • {recommendations.filter(rec => !hiddenRecommendations.has(rec.id)).length} new
              </span>
              <span className="text-sm text-gray-500">User: {userId}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Subscription Status Banner */}
        {subscriptionStatus === 'trial_expired' && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div>
                  <h3 className="text-red-800 font-semibold">Trial Expired</h3>
                  <p className="text-red-700 text-sm">Your 7-day trial has ended. Upgrade to continue using the AI assistant.</p>
                </div>
              </div>
              <button
                onClick={handleUpgrade}
                disabled={isUpgrading}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {isUpgrading ? 'Loading...' : 'Upgrade - $9.99/month'}
              </button>
            </div>
          </div>
        )}

        {subscriptionStatus === 'trial' && trialEndsAt && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-yellow-600" />
                <div>
                  <h3 className="text-yellow-800 font-semibold">Free Trial Active</h3>
                  <p className="text-yellow-700 text-sm">
                    Trial ends {trialEndsAt.toLocaleDateString()} at {trialEndsAt.toLocaleTimeString()}
                  </p>
                </div>
              </div>
              <button
                onClick={handleUpgrade}
                disabled={isUpgrading}
                className="px-6 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {isUpgrading ? 'Loading...' : 'Upgrade Early - $9.99/month'}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-medium text-gray-900">Navigation</h2>
              </div>
              <nav className="p-2">
                <button
                  onClick={() => setActiveTab('recommendations')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'recommendations'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Star className="w-4 h-4 inline mr-2" />
                  New Recommendations ({recommendations.filter(rec => !hiddenRecommendations.has(rec.id)).length})
                </button>
                <button
                  onClick={() => setActiveTab('saved')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'saved'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Archive className="w-4 h-4 inline mr-2" />
                  Saved Recommendations ({savedRecommendationIds.size})
                </button>
                <button
                  onClick={() => setActiveTab('preferences')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'preferences'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Settings className="w-4 h-4 inline mr-2" />
                  Scanning Preferences ({preferences.length})
                </button>
                <button
                  onClick={() => setActiveTab('channels')}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'channels'
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Monitored Channels ({monitoredChannels.length})
                </button>
              </nav>
            </div>

            {/* Quick Stats */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 mb-3">Live Stats</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Active preferences</span>
                  <span className="font-medium">{preferences.filter(p => p.active).length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">New matches today</span>
                  <span className="font-medium text-purple-600">{recommendations.filter(r => {
                    const today = new Date().toDateString();
                    return new Date(r.created_at).toDateString() === today && !hiddenRecommendations.has(r.id);
                  }).length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Channels monitored</span>
                  <span className="font-medium">{monitoredChannels.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            {activeTab === 'recommendations' && (
              <div>
                {/* Header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Recommendations</h2>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>Live monitoring</span>
                      <span>•</span>
                      <span>Last updated: {new Date().toLocaleTimeString()}</span>
                    </div>
                  </div>
                  

                  {/* Toolbar */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      {/* Left side - Filters */}
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setShowFilters(!showFilters)}
                          className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 font-medium ${
                            showFilters
                              ? 'bg-purple-100 text-purple-700 border border-purple-300'
                              : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Filter className="w-4 h-4" />
                          <span>Filters</span>
                          {Object.values(filters).some(v => v !== 'all' && v !== false && v !== '') && (
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          )}
                        </button>
                        
                        {Object.values(filters).some(v => v !== 'all' && v !== false && v !== '') && (
                          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                            Filters active
                          </span>
                        )}
                      </div>
                      
                      {/* Right side - Analysis controls */}
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-3 bg-gray-50 rounded-lg px-4 py-2">
                          <label className="text-sm font-medium text-gray-700">Check the last:</label>
                          <select
                            value={fetchDays}
                            onChange={(e) => setFetchDays(parseInt(e.target.value))}
                            className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                          >
                            <option value={1}>1 day</option>
                            <option value={3}>3 days</option>
                            <option value={7}>1 week</option>
                            <option value={14}>2 weeks</option>
                          </select>
                        </div>
                        
                        <button
                          onClick={fetchRecommendationsManually}
                          disabled={fetchingRecommendations || preferences.length === 0 || selectedPreferencesForScanning.size === 0}
                          className={`px-4 py-2 text-sm rounded-md flex items-center space-x-2 font-medium shadow-sm transition-all duration-200 ${
                            hasNewRecommendations
                              ? 'bg-green-600 text-white hover:bg-green-700 animate-pulse'
                              : fetchingRecommendations
                              ? 'bg-purple-600 text-white'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          title={selectedPreferencesForScanning.size === 0 ? "Select at least one preference to scan for" : `Check for new recommendations from the last ${fetchDays} day${fetchDays > 1 ? 's' : ''}.`}
                        >
                          {fetchingRecommendations ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Analyzing...</span>
                            </>
                          ) : hasNewRecommendations ? (
                            <>
                              <Bell className="w-4 h-4" />
                              <span>New Available!</span>
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4" />
                              <span>Check for New</span>
                            </>
                          )}
                        </button>
                        
                        {recommendations.length > 0 && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to clear all ${recommendations.length} recommendations? This action cannot be undone.`)) {
                                clearAllRecommendations();
                              }
                            }}
                            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center space-x-2 font-medium shadow-sm transition-all duration-200"
                            title="Clear all current recommendations"
                          >
                            <X className="w-4 h-4" />
                            <span>Clear All</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Scanning for preferences */}
                  {preferences.filter(p => p.active).length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-700">Scanning for:</span>
                        <span className="text-xs text-gray-500">
                          ({selectedPreferencesForScanning.size} of {preferences.filter(p => p.active).length} selected)
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {preferences.filter(p => p.active).map((pref) => {
                          const isSelected = selectedPreferencesForScanning.has(pref.id);
                          return (
                            <button
                              key={pref.id}
                              onClick={() => togglePreferenceForScanning(pref.id)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 hover:shadow-sm ${
                                isSelected
                                  ? pref.urgency === 'high'
                                    ? 'text-red-700 bg-red-100 border-red-300 shadow-sm'
                                    : pref.urgency === 'medium'
                                    ? 'text-yellow-700 bg-yellow-100 border-yellow-300 shadow-sm'
                                    : 'text-green-700 bg-green-100 border-green-300 shadow-sm'
                                  : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100'
                              }`}
                              title={isSelected ? `Click to exclude "${pref.keyword}" from scanning` : `Click to include "${pref.keyword}" in scanning`}
                            >
                              {pref.keyword}
                              {isSelected && <span className="ml-1">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                      {selectedPreferencesForScanning.size === 0 && (
                        <p className="text-xs text-orange-600 mt-2 flex items-center">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Select at least one preference to enable fetching recommendations
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Filter Panel */}
                {showFilters && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-gray-900">Filter Recommendations</h3>
                      <button
                        onClick={clearFilters}
                        className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      {/* Search */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                          type="text"
                          value={filters.searchText}
                          onChange={(e) => setFilters({...filters, searchText: e.target.value})}
                          placeholder="Search in title, content..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                      </div>
                      
                      {/* Preference Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Preference</label>
                        <select
                          value={filters.preference}
                          onChange={(e) => setFilters({...filters, preference: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          <option value="all">All Preferences</option>
                          {getUniquePreferences().map(pref => (
                            <option key={pref} value={pref}>{pref}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Channel Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                        <select
                          value={filters.channel}
                          onChange={(e) => setFilters({...filters, channel: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          <option value="all">All Channels</option>
                          {getUniqueChannels().map(channel => (
                            <option key={channel} value={channel}>#{channel}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Confidence Filter */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confidence</label>
                        <select
                          value={filters.confidence}
                          onChange={(e) => setFilters({...filters, confidence: e.target.value})}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          <option value="all">All Confidence</option>
                          <option value="high">High (90%+)</option>
                          <option value="medium">Medium (70-89%)</option>
                          <option value="low">Low (&lt;70%)</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Content Type Filters */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filters.hasPrice}
                            onChange={(e) => setFilters({...filters, hasPrice: e.target.checked})}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">Has Price</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filters.hasLocation}
                            onChange={(e) => setFilters({...filters, hasLocation: e.target.checked})}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">Has Location</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filters.hasDate}
                            onChange={(e) => setFilters({...filters, hasDate: e.target.checked})}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">Has Date/Time</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={filters.hasLinks}
                            onChange={(e) => setFilters({...filters, hasLinks: e.target.checked})}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm text-gray-700">Has Links</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {preferences.length === 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      <p className="text-sm text-yellow-800">
                        Add some preferences first to enable manual recommendation fetching.
                      </p>
                    </div>
                  </div>
                )}

                {(() => {
                  const filteredRecs = filterRecommendations(recommendations);
                  return filteredRecs.length === 0;
                })() ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    {recommendations.length === 0 ? (
                      <>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No recommendations right now...</h3>
                        <p className="text-gray-600 mb-4">Add new scanning preferences to find more relevant posts or add new channels to scan!</p>
                        <button
                          onClick={() => setActiveTab('preferences')}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                        >
                          Add a Scanning Preference
                        </button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">All recommendations hidden</h3>
                        <p className="text-gray-600 mb-4">You've hidden all {recommendations.length} recommendations. New ones will appear here as they're found.</p>
                        <button
                          onClick={() => setHiddenRecommendations(new Set())}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                        >
                          Show All Recommendations
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const filteredRecs = filterRecommendations(recommendations);
                      return (
                        <>
                          {filteredRecs.length < recommendations.filter(rec => !hiddenRecommendations.has(rec.id)).length && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Filter className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm text-blue-800">
                                    Showing {filteredRecs.length} of {recommendations.filter(rec => !hiddenRecommendations.has(rec.id)).length} recommendations
                                  </span>
                                </div>
                                <button
                                  onClick={clearFilters}
                                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  Clear Filters
                                </button>
                              </div>
                            </div>
                          )}
                          {filteredRecs.map((rec) => {
                            const messageData = parseMessageData(rec.message_text);
                            const extractedItems = renderExtractedInfo(messageData.extractedInfo);
                            const isExpanded = expandedRecommendations.has(rec.id);
                            
                            return (
                              <div key={rec.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          {/* Header with preference tag */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-2">
                              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                                {rec.preference_keyword}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(rec.confidence)}`}>
                                {Math.round(rec.confidence)}% match
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <span>#{rec.channel_name}</span>
                              <span>•</span>
                              <span>@{rec.user_name}</span>
                              <span>•</span>
                              <span>{new Date(rec.timestamp).toLocaleDateString()}</span>
                            </div>
                          </div>
                          
                          {/* Title and Snippet */}
                          <div className="mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {messageData.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {messageData.snippet}
                            </p>
                          </div>
                          
                          {/* Extracted Information */}
                          {extractedItems.length > 0 && (
                            <div className="mb-4 p-3 bg-gray-50 rounded-md">
                              <div className="grid grid-cols-2 gap-3">
                                {extractedItems.map((item, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <item.icon className={`w-4 h-4 ${item.color}`} />
                                    <span className="text-sm font-medium text-gray-700">{item.label}:</span>
                                    {item.isLink ? (
                                      <a 
                                        href={item.value}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-indigo-600 hover:text-indigo-800 underline break-all"
                                      >
                                        {item.value}
                                      </a>
                                    ) : (
                                      <span className="text-sm text-gray-900">{item.value}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Expandable explanation */}
                          <div className="mb-4">
                            <button
                              onClick={() => toggleRecommendationExpansion(rec.id)}
                              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                              <span>Why this was flagged</span>
                            </button>
                            
                            {isExpanded && (
                              <div className="mt-2 p-3 bg-blue-50 rounded-md border border-blue-200">
                                <p className="text-sm text-blue-800 mb-2">
                                  <strong>AI Reasoning:</strong> {rec.reasoning}
                                </p>
                                <details className="text-sm text-gray-600">
                                  <summary className="cursor-pointer hover:text-gray-800 font-medium">
                                    View original message
                                  </summary>
                                  <div className="mt-2 p-2 bg-white rounded border text-gray-800">
                                    {messageData.originalText}
                                  </div>
                                </details>
                              </div>
                            )}
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openInSlack(rec.channel_id, rec.slack_message_ts)}
                              className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors flex items-center space-x-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              <span>Open in Slack</span>
                            </button>
                            <button
                              onClick={() => {
                                console.log(`🔘 Save button clicked for recommendation ${rec.id}`);
                                console.log(`🔍 Current savedRecommendationIds:`, Array.from(savedRecommendationIds));
                                console.log(`🔍 savedRecommendationIds type:`, typeof savedRecommendationIds);
                                console.log(`🔍 savedRecommendationIds size:`, savedRecommendationIds.size);
                                console.log(`🔍 rec.id value:`, rec.id, `(type: ${typeof rec.id})`);
                                console.log(`🔍 Is ${rec.id} in saved set?`, savedRecommendationIds.has(rec.id));
                                
                                // Test different type conversions
                                console.log(`🔍 Testing type conversions:`);
                                console.log(`🔍   has(${rec.id}):`, savedRecommendationIds.has(rec.id));
                                console.log(`🔍   has("${rec.id}"):`, savedRecommendationIds.has(String(rec.id)));
                                console.log(`🔍   has(${parseInt(rec.id)}):`, savedRecommendationIds.has(parseInt(rec.id)));
                                
                                saveRecommendation(rec.id);
                              }}
                              disabled={(() => {
                                const normalizedRecId = typeof rec.id === 'string' && !isNaN(rec.id) ? parseInt(rec.id) : rec.id;
                                return savedRecommendationIds.has(normalizedRecId) || savedRecommendationIds.has(rec.id);
                              })()}
                              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                (() => {
                                  const normalizedRecId = typeof rec.id === 'string' && !isNaN(rec.id) ? parseInt(rec.id) : rec.id;
                                  const isSaved = savedRecommendationIds.has(normalizedRecId) || savedRecommendationIds.has(rec.id);
                                  return isSaved
                                    ? 'bg-green-100 text-green-700 border border-green-300 cursor-not-allowed'
                                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50';
                                })()
                              }`}
                            >
                              {(() => {
                                const normalizedRecId = typeof rec.id === 'string' && !isNaN(rec.id) ? parseInt(rec.id) : rec.id;
                                const isSaved = savedRecommendationIds.has(normalizedRecId) || savedRecommendationIds.has(rec.id);
                                return isSaved ? 'Saved' : 'Save';
                              })()}
                            </button>
                            <button
                              onClick={() => markNotInterested(rec.id)}
                              className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition-colors"
                            >
                              Not interested
                            </button>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'preferences' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Scanning Preferences</h2>
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Preference</span>
                  </button>
                </div>

                {showAddForm && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h3 className="font-medium text-gray-900 mb-4">Add New Preference</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Keyword/Topic</label>
                        <input
                          type="text"
                          value={newPreference.keyword}
                          onChange={(e) => setNewPreference({...newPreference, keyword: e.target.value})}
                          placeholder="e.g., chair, laptop, apartment"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                        <select
                          value={newPreference.urgency}
                          onChange={(e) => setNewPreference({...newPreference, urgency: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={newPreference.description}
                        onChange={(e) => setNewPreference({...newPreference, description: e.target.value})}
                        placeholder="Describe what you're looking for in detail..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        rows="2"
                      />
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Channels to Monitor</label>
                        <div className="flex items-center space-x-2">
                          {newPreference.channels.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to remove all selected channels?')) {
                                  setNewPreference({
                                    ...newPreference,
                                    channels: []
                                  });
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Remove All
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* User-specific channel search */}
                      <div className="mb-3 p-3 bg-gray-50 rounded-md">
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Load channels for a specific user:
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={targetUserId}
                            onChange={(e) => setTargetUserId(e.target.value)}
                            placeholder="Enter user ID (e.g., U1234567890)"
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                          <button
                            type="button"
                            onClick={() => fetchChannelsForUser(targetUserId)}
                            disabled={channelsLoading || !targetUserId.trim()}
                            className="px-2 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                          >
                            {channelsLoading ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>Loading...</span>
                              </>
                            ) : (
                              <span>Load User's Channels</span>
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          This will show only channels that the specified user is a member of for you to choose from.
                          <br />
                          <span className="text-blue-600">💡 Channels will only be added to monitoring when you check their boxes and click "Save Preference".</span>
                        </p>
                      </div>
                      
                      {/* Selected Channels Display */}
                      {newPreference.channels.length > 0 && (
                        <div className="mb-3 p-2 bg-purple-50 rounded-md">
                          <div className="text-xs font-medium text-purple-700 mb-1">Selected Channels:</div>
                          <div className="flex flex-wrap gap-1">
                            {newPreference.channels.map((channelId) => {
                              const channel = channels.find(c => c.id === channelId);
                              return channel ? (
                                <span
                                  key={channelId}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800"
                                >
                                  #{channel.name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewPreference({
                                        ...newPreference,
                                        channels: newPreference.channels.filter(id => id !== channelId)
                                      });
                                    }}
                                    className="ml-1 text-purple-600 hover:text-purple-800"
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Check All / Uncheck All Buttons */}
                      {channels.length > 0 && (
                        <div className="mb-3 flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              const allChannelIds = channels.map(c => c.id);
                              setNewPreference({...newPreference, channels: allChannelIds});
                            }}
                            className="text-xs text-green-600 hover:text-green-800 font-medium"
                          >
                            Check All
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setNewPreference({...newPreference, channels: []});
                            }}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Uncheck All
                          </button>
                        </div>
                      )}
                      
                      {channels.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No channels available.</p>
                          <p className="text-xs mt-1">Channels will be automatically loaded from your workspace.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                          {channels.map((channel) => (
                            <label key={channel.id} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={newPreference.channels.includes(channel.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewPreference({
                                      ...newPreference,
                                      channels: [...newPreference.channels, channel.id]
                                    });
                                  } else {
                                    setNewPreference({
                                      ...newPreference,
                                      channels: newPreference.channels.filter(id => id !== channel.id)
                                    });
                                  }
                                }}
                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-700">#{channel.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 mt-4">
                      <button
                        onClick={addPreference}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                      >
                        Save Preference
                      </button>
                      <button
                        onClick={() => setShowAddForm(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {editingPreference && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h3 className="font-medium text-gray-900 mb-4">Edit Preference</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Keyword/Topic</label>
                        <input
                          type="text"
                          value={editingPreference.keyword}
                          onChange={(e) => setEditingPreference({...editingPreference, keyword: e.target.value})}
                          placeholder="e.g., chair, laptop, apartment"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
                        <select
                          value={editingPreference.urgency}
                          onChange={(e) => setEditingPreference({...editingPreference, urgency: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editingPreference.description}
                        onChange={(e) => setEditingPreference({...editingPreference, description: e.target.value})}
                        placeholder="Describe what you're looking for in detail..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        rows="2"
                      />
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Channels to Monitor</label>
                        <div className="flex items-center space-x-2">
                          {editingPreference.channels.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to remove all selected channels?')) {
                                  setEditingPreference({
                                    ...editingPreference,
                                    channels: []
                                  });
                                }
                              }}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Remove All
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* User-specific channel search */}
                      <div className="mb-3 p-3 bg-gray-50 rounded-md">
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Load channels for a specific user:
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={targetUserId}
                            onChange={(e) => setTargetUserId(e.target.value)}
                            placeholder="Enter user ID (e.g., U1234567890)"
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                          <button
                            type="button"
                            onClick={() => fetchChannelsForUser(targetUserId)}
                            disabled={channelsLoading || !targetUserId.trim()}
                            className="px-2 py-1 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                          >
                            {channelsLoading ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>Loading...</span>
                              </>
                            ) : (
                              <span>Load User's Channels</span>
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          This will show only channels that the specified user is a member of for you to choose from.
                          <br />
                          <span className="text-blue-600">💡 Channels will only be added to monitoring when you check their boxes and click "Update Preference".</span>
                        </p>
                      </div>
                      
                      {/* Selected Channels Display */}
                      {editingPreference.channels.length > 0 && (
                        <div className="mb-3 p-2 bg-purple-50 rounded-md">
                          <div className="text-xs font-medium text-purple-700 mb-1">Selected Channels:</div>
                          <div className="flex flex-wrap gap-1">
                            {editingPreference.channels.map((channelId) => {
                              const channel = channels.find(c => c.id === channelId);
                              return channel ? (
                                <span
                                  key={channelId}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800"
                                >
                                  #{channel.name}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingPreference({
                                        ...editingPreference,
                                        channels: editingPreference.channels.filter(id => id !== channelId)
                                      });
                                    }}
                                    className="ml-1 text-purple-600 hover:text-purple-800"
                                  >
                                    ×
                                  </button>
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Check All / Uncheck All Buttons */}
                      {channels.length > 0 && (
                        <div className="mb-3 flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              const allChannelIds = channels.map(c => c.id);
                              setEditingPreference({...editingPreference, channels: allChannelIds});
                            }}
                            className="text-xs text-green-600 hover:text-green-800 font-medium"
                          >
                            Check All
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingPreference({...editingPreference, channels: []});
                            }}
                            className="text-xs text-red-600 hover:text-red-800 font-medium"
                          >
                            Uncheck All
                          </button>
                        </div>
                      )}
                      
                      {channels.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <p className="text-sm">No channels available.</p>
                          <p className="text-xs mt-1">Channels will be automatically loaded from your workspace.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                          {channels.map((channel) => (
                            <label key={channel.id} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={editingPreference.channels.includes(channel.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditingPreference({
                                      ...editingPreference,
                                      channels: [...editingPreference.channels, channel.id]
                                    });
                                  } else {
                                    setEditingPreference({
                                      ...editingPreference,
                                      channels: editingPreference.channels.filter(id => id !== channel.id)
                                    });
                                  }
                                }}
                                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-700">#{channel.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 mt-4">
                      <button
                        onClick={updatePreference}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                      >
                        Update Preference
                      </button>
                      <button
                        onClick={() => setEditingPreference(null)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {preferences.map((pref) => {
                    const prefChannels = JSON.parse(pref.channels || '[]');
                    // eslint-disable-next-line no-unused-vars
                    const channelNames = prefChannels.map(channelId => {
                      const channel = channels.find(c => c.id === channelId);
                      return channel ? channel.name : channelId;
                    });
                    
                    return (
                      <div key={pref.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-medium text-gray-900">{pref.keyword}</h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getUrgencyColor(pref.urgency)}`}>
                                {pref.urgency} priority
                              </span>
                              {pref.active && (
                                <span className="px-2 py-1 bg-green-50 text-green-600 rounded-full text-xs font-medium">
                                  Active
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 text-sm mb-3">{pref.description}</p>
                            
                            {/* Channels Display */}
                            <div className="mb-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-500">
                                  Monitoring: {prefChannels.length} channels
                                </span>
                                {prefChannels.length > 0 && (
                                  <button
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to remove all channels from "${pref.keyword}"? This will stop monitoring all channels for this preference.`)) {
                                        // Update preference to remove all channels
                                        fetch(`${API_BASE}/api/preferences/${pref.id}`, {
                                          method: 'PUT',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
                                          },
                                          body: JSON.stringify({
                                            ...pref,
                                            channels: JSON.stringify([])
                                          })
                                        }).then(() => {
                                          fetchPreferences();
                                          toast.success('All channels removed from preference');
                                        }).catch(() => {
                                          toast.error('Failed to update preference');
                                        });
                                      }
                                    }}
                                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                                  >
                                    Remove All Channels
                                  </button>
                                )}
                              </div>
                              
                              {prefChannels.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {prefChannels.map((channelId) => {
                                    const channel = channels.find(c => c.id === channelId);
                                    return channel ? (
                                      <span
                                        key={channelId}
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                                      >
                                        #{channel.name}
                                        <button
                                          onClick={() => {
                                            const updatedChannels = prefChannels.filter(id => id !== channelId);
                                            fetch(`${API_BASE}/api/preferences/${pref.id}`, {
                                              method: 'PUT',
                                              headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
                                              },
                                              body: JSON.stringify({
                                                ...pref,
                                                channels: JSON.stringify(updatedChannels)
                                              })
                                            }).then(() => {
                                              fetchPreferences();
                                              toast.success(`Removed #${channel.name} from monitoring`);
                                            }).catch(() => {
                                              toast.error('Failed to update preference');
                                            });
                                          }}
                                          className="ml-1 text-blue-600 hover:text-blue-800"
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ) : (
                                      <span
                                        key={channelId}
                                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600"
                                      >
                                        {channelId}
                                        <button
                                          onClick={() => {
                                            const updatedChannels = prefChannels.filter(id => id !== channelId);
                                            fetch(`${API_BASE}/api/preferences/${pref.id}`, {
                                              method: 'PUT',
                                              headers: {
                                                'Content-Type': 'application/json',
                                                'Authorization': `Bearer ${localStorage.getItem('slack_token') || userId}`
                                              },
                                              body: JSON.stringify({
                                                ...pref,
                                                channels: JSON.stringify(updatedChannels)
                                              })
                                            }).then(() => {
                                              fetchPreferences();
                                              toast.success('Removed channel from monitoring');
                                            }).catch(() => {
                                              toast.error('Failed to update preference');
                                            });
                                          }}
                                          className="ml-1 text-gray-600 hover:text-gray-800"
                                        >
                                          ×
                                        </button>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => startEditingPreference(pref)}
                              className="text-gray-400 hover:text-blue-500 transition-colors"
                              title="Edit preference"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete the preference "${pref.keyword}"? This action cannot be undone.`)) {
                                  deletePreference(pref.id);
                                }
                              }}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                              title="Delete preference"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'channels' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Monitored Channels</h2>
                  <div className="text-sm text-gray-500">
                    {monitoredChannels.length} channels being monitored from your preferences
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {monitoredChannels.map((channel) => (
                    <div key={channel.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-medium text-gray-900">#{channel.name}</h3>
                            {channel.is_member && (
                              <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs font-medium">
                                Joined
                              </span>
                            )}
                            {channel.is_general && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                                General
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mb-2">
                            {channel.num_members || 0} members
                          </div>
                          <div className="text-xs text-gray-500 mb-3">
                            {channel.purpose?.value || 'No description'}
                          </div>
                        </div>
                        <MessageSquare className="w-4 h-4 text-gray-400 mt-1" />
                      </div>
                      
                      {!channel.is_member ? (
                        <button
                          onClick={() => joinChannel(channel.id, channel.name)}
                          className="w-full px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors"
                        >
                          Join Channel
                        </button>
                      ) : (
                        <button
                          onClick={() => leaveChannel(channel.id, channel.name)}
                          className="w-full px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                        >
                          Leave Channel
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'saved' && (
              <SavedRecommendations
                userId={userId}
                onSaveRecommendation={setRefreshSavedRecommendations}
                onUnsaveRecommendation={handleUnsaveRecommendation}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Setup/Onboarding Component
const Setup = () => {
  const connectToSlack = () => {
    window.location.href = `${API_BASE}/auth/slack`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Kellogg Slack AI Assistant</h1>
          <p className="text-gray-600 mb-8">
            Connect your Slack workspace to get AI-powered recommendations from your channels.
          </p>
          
          <button
            onClick={connectToSlack}
            className="w-full bg-purple-600 text-white py-3 px-4 rounded-md hover:bg-purple-700 transition-colors font-medium"
          >
            Connect to Slack
          </button>
          
          <p className="text-xs text-gray-500 mt-4">
            We'll only access channels you're already a member of
          </p>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check URL params for Slack OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const connectedUser = urlParams.get('user');
    const connected = urlParams.get('connected');
    const error = urlParams.get('error');
    const refresh = urlParams.get('refresh');

    if (connected === 'true' && connectedUser) {
      setUserId(connectedUser);
      localStorage.setItem('slack_user_id', connectedUser);
      
      if (refresh === 'true') {
        toast.success('Slack connection refreshed with updated permissions!');
        // Remove the temp user ID since we successfully refreshed
        localStorage.removeItem('slack_user_id_temp');
      } else {
        toast.success('Successfully connected to Slack!');
      }
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      toast.error('Failed to connect to Slack');
      
      // If this was a refresh attempt, restore the previous user ID
      const tempUserId = localStorage.getItem('slack_user_id_temp');
      if (tempUserId) {
        setUserId(tempUserId);
        localStorage.setItem('slack_user_id', tempUserId);
        localStorage.removeItem('slack_user_id_temp');
      }
    } else {
      // Check localStorage for existing user
      const savedUserId = localStorage.getItem('slack_user_id');
      if (savedUserId) {
        setUserId(savedUserId);
      }
    }
    
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" />
        <Routes>
          <Route
            path="/"
            element={userId ? <Dashboard userId={userId} /> : <Setup />}
          />
          <Route path="/success" element={<CheckoutSuccess />} />
          <Route path="/cancel" element={<CheckoutCancel />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;